import { Component, type ErrorInfo, type ReactNode } from 'react'
import { AlertTriangle } from 'lucide-react'

interface Props {
  children: ReactNode
  /** when this changes (the route), the error clears */
  resetKey?: string
  onError?: (error: Error, info: ErrorInfo) => void
}

interface State {
  error: Error | null
}

/** A render error shows a friendly card instead of a white screen; navigating away clears it. */
export class ErrorBoundary extends Component<Props, State> {
  override state: State = { error: null }

  static getDerivedStateFromError(error: Error): State {
    return { error }
  }

  override componentDidCatch(error: Error, info: ErrorInfo) {
    this.props.onError?.(error, info)
    console.error('[ErrorBoundary]', error, info.componentStack)
  }

  override componentDidUpdate(prev: Props) {
    if (prev.resetKey !== this.props.resetKey && this.state.error) this.setState({ error: null })
  }

  override render() {
    if (!this.state.error) return this.props.children
    return (
      <div
        role="alert"
        className="m-4 card flex items-start gap-3 border-red-200 bg-red-50 dark:border-red-900/50 dark:bg-red-950/30"
      >
        <AlertTriangle size={20} className="mt-0.5 shrink-0 text-red-600 dark:text-red-400" />
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold text-red-800 dark:text-red-200">This screen hit an error</p>
          <p className="mt-1 text-xs text-red-700 dark:text-red-300">{this.state.error.message}</p>
          <div className="mt-3 flex gap-2">
            <button
              type="button"
              className="btn btn-secondary btn-sm"
              onClick={() => this.setState({ error: null })}
            >
              Try again
            </button>
            <button
              type="button"
              className="btn btn-secondary btn-sm"
              onClick={() => window.location.reload()}
            >
              Reload the page
            </button>
          </div>
        </div>
      </div>
    )
  }
}
